import { Link } from "react-router-dom";
import { Star, BadgeCheck, Download, KeyRound, Hand, Shirt, Coins, Mail, ShieldCheck } from "lucide-react";
import { mediaSrc, money, LOGO_URL, num } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useEffect as _useEffect, useState as _useState } from "react";

const inline = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
  part.startsWith("**") && part.endsWith("**") && part.length > 4 ? <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong> : part);

export function RichText({ text, className, testId }) {
  const blocks = [];
  (text || "").split("\n").forEach((line, i) => {
    const t = line.trim();
    if (t.startsWith("## ")) blocks.push(<h3 key={i} className="mt-4 text-base font-semibold text-white first:mt-0">{inline(t.slice(3))}</h3>);
    else if (/^[-•] /.test(t)) blocks.push(<li key={i} className="ml-5 list-disc">{inline(t.slice(2))}</li>);
    else if (!t) blocks.push(<div key={i} className="h-3" />);
    else blocks.push(<p key={i}>{inline(line)}</p>);
  });
  return <div className={className} data-testid={testId}>{blocks}</div>;
}

export function Countdown({ to, className, testId = "countdown" }) {
  const [t, setT] = _useState(() => Date.parse(to) - Date.now());
  _useEffect(() => { const i = setInterval(() => setT(Date.parse(to) - Date.now()), 1000); return () => clearInterval(i); }, [to]);
  if (!to || t <= 0) return null;
  const parts = [["d", Math.floor(t / 864e5)], ["h", Math.floor(t / 36e5) % 24], ["m", Math.floor(t / 6e4) % 60], ["s", Math.floor(t / 1e3) % 60]];
  return (
    <div data-testid={testId} className={cn("flex gap-1.5 font-mono", className)}>
      {parts.map(([k, v]) => <span key={k} className="rounded-md border border-violet-500/30 bg-black/40 px-2 py-1 text-sm text-white tabular-nums">{String(v).padStart(2, "0")}<span className="ml-0.5 text-[10px] text-violet-300">{k}</span></span>)}
    </div>
  );
}

export const Logo = ({ className }) => (
  <Link to="/" data-testid="nav-logo" className={cn("flex items-center gap-2.5 group", className)}>
    <img src={LOGO_URL} alt="Vaulted" className="h-8 w-8 rounded-full ring-1 ring-violet-500/40 group-hover:ring-violet-400 transition-[box-shadow]" />
    <span className="text-[15px] font-semibold tracking-[0.22em] text-white">VAULTED</span>
  </Link>
);

export const Stars = ({ value = 0, size = 12, className }) => (
  <div className={cn("flex items-center gap-0.5", className)}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} style={{ width: size, height: size }} className={i <= Math.round(value) ? "fill-violet-400 text-violet-400" : "text-zinc-700"} />
    ))}
  </div>
);

const DeliveryIcon = { download: Download, licence_key: KeyRound, manual: Hand };

export const FnSpecs = ({ fn, className }) => fn ? (
  <div className={cn("flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-zinc-400", className)}>
    <span className="flex items-center gap-1"><Shirt className="h-3 w-3 text-violet-400" />{num(fn.skins_count)} skins</span>
    {fn.vbucks > 0 && <span className="flex items-center gap-1"><Coins className="h-3 w-3 text-violet-400" />{num(fn.vbucks)}</span>}
    {fn.email_changeable && <span title="Email changeable" className="flex items-center gap-1 text-emerald-400"><Mail className="h-3 w-3" />EC</span>}
    {fn.full_access && <span title="Full access" className="flex items-center gap-1 text-emerald-400"><ShieldCheck className="h-3 w-3" />FA</span>}
  </div>
) : null;

export function ProductCard({ p, index = 0 }) {
  const Icon = DeliveryIcon[p.delivery_method] || Download;
  return (
    <Link to={`/product/${p.slug || p.id}`} data-testid={`product-card-${p.id}`}
      className="group v-card overflow-hidden flex flex-col rise hover:border-violet-500/50 hover:-translate-y-0.5 transition-[border-color,transform] duration-200"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}>
      <div className="relative aspect-video bg-[#111116] overflow-hidden">
        {p.thumbnail ? (
          <img src={mediaSrc(p.thumbnail)} alt={p.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
        ) : (
          <div className="h-full w-full grid place-items-center grid-lines"><Icon className="h-6 w-6 text-zinc-700" /></div>
        )}
        {!p.in_stock && <span className="absolute left-2 top-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-zinc-300">Out of stock</span>}
        {p.featured && <span className="absolute right-2 top-2 rounded bg-violet-600/90 px-1.5 py-0.5 text-[10px] font-medium text-white">Featured</span>}
      </div>
      <div className="flex flex-1 flex-col p-3">
        <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
          <span className="truncate">{p.category?.name || "Uncategorised"}</span>
          <span>·</span>
          <Icon className="h-3 w-3" />
        </div>
        <h3 className="mt-1 line-clamp-1 text-sm font-medium text-white group-hover:text-violet-200 transition-colors">{p.title}</h3>
        <div className="mt-0.5 flex items-center gap-1 text-xs text-zinc-400">
          <span className="truncate">{p.seller?.store_name}</span>
          {p.seller?.is_official && <BadgeCheck className="h-3.5 w-3.5 text-violet-400 shrink-0" />}
        </div>
        <FnSpecs fn={p.fortnite} className="mt-2" />
        {p.fortnite?.platforms?.length > 0 && <p className="mt-1 truncate text-[10px] uppercase tracking-wider text-zinc-600">{p.fortnite.platforms.join(" · ")}</p>}
        <div className="mt-auto flex items-end justify-between pt-3">
          <span className="text-[15px] font-semibold text-white" data-testid={`product-card-price-${p.id}`}>{money(p.price)}</span>
          {p.rating_count > 0 ? (
            <span className="flex items-center gap-1 text-[11px] text-zinc-400"><Star className="h-3 w-3 fill-violet-400 text-violet-400" />{p.rating_avg.toFixed(1)} ({p.rating_count})</span>
          ) : (
            <span className="text-[11px] text-zinc-600">No reviews yet</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export const ProductGrid = ({ items, testId = "product-grid" }) => (
  <div data-testid={testId} className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
    {items.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
  </div>
);

export const ProductSkeleton = ({ n = 4 }) => (
  <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className="v-card overflow-hidden"><div className="aspect-video bg-zinc-900 animate-pulse" /><div className="p-3 space-y-2"><div className="h-3 w-1/3 bg-zinc-800 rounded animate-pulse" /><div className="h-3.5 w-3/4 bg-zinc-800 rounded animate-pulse" /><div className="h-3 w-1/4 bg-zinc-800 rounded animate-pulse" /></div></div>
    ))}
  </div>
);

export const EmptyState = ({ icon: Icon, title, body, action, testId }) => (
  <div data-testid={testId} className="v-panel flex flex-col items-center justify-center px-6 py-12 text-center">
    {Icon && <div className="mb-3 grid h-10 w-10 place-items-center rounded-lg border border-[#27272A] bg-[#18181B]"><Icon className="h-5 w-5 text-violet-400" /></div>}
    <p className="text-sm font-medium text-white">{title}</p>
    {body && <p className="mt-1 max-w-sm text-sm text-zinc-500">{body}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

const STATUS_STYLES = {
  PAID: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", COMPLETED: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  PROCESSING: "text-violet-300 bg-violet-500/10 border-violet-500/20", PENDING: "text-amber-300 bg-amber-500/10 border-amber-500/20",
  CANCELLED: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20", REFUNDED: "text-sky-300 bg-sky-500/10 border-sky-500/20",
  DISPUTED: "text-red-400 bg-red-500/10 border-red-500/20",
  active: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", approved: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  completed: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", delivered: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  fulfilled: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", visible: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  pending: "text-amber-300 bg-amber-500/10 border-amber-500/20", awaiting_fulfilment: "text-amber-300 bg-amber-500/10 border-amber-500/20",
  info_requested: "text-amber-300 bg-amber-500/10 border-amber-500/20", processing: "text-violet-300 bg-violet-500/10 border-violet-500/20",
  open: "text-red-400 bg-red-500/10 border-red-500/20", draft: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20",
  disabled: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20", rejected: "text-red-400 bg-red-500/10 border-red-500/20",
  suspended: "text-red-400 bg-red-500/10 border-red-500/20", removed: "text-red-400 bg-red-500/10 border-red-500/20",
  resolved: "text-zinc-300 bg-zinc-500/10 border-zinc-500/20", expired: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20",
};

export const StatusBadge = ({ status, testId }) => (
  <span data-testid={testId} className={cn("inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide", STATUS_STYLES[status] || "text-zinc-300 border-zinc-700")}>
    {String(status || "").replace(/_/g, " ")}
  </span>
);

export const SectionHeader = ({ eyebrow, title, action }) => (
  <div className="mb-5 flex items-end justify-between gap-4">
    <div>
      {eyebrow && <p className="v-eyebrow mb-1.5">{eyebrow}</p>}
      <h2 className="text-lg font-semibold tracking-tight text-white">{title}</h2>
    </div>
    {action}
  </div>
);

export const StatCard = ({ label, value, icon: Icon, hint, testId }) => (
  <div data-testid={testId} className="v-card p-4">
    <div className="flex items-center justify-between">
      <span className="text-xs text-zinc-400">{label}</span>
      {Icon && <Icon className="h-4 w-4 text-violet-400" />}
    </div>
    <div className="mt-2 text-xl font-semibold tracking-tight text-white">{value}</div>
    {hint && <div className="mt-0.5 text-[11px] text-zinc-500">{hint}</div>}
  </div>
);

export const PageTitle = ({ title, subtitle, action }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
    <div>
      <h1 className="text-xl font-semibold tracking-tight text-white">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Field = ({ label, hint, children, className }) => (
  <label className={cn("block", className)}>
    <span className="mb-1.5 block text-xs font-medium text-zinc-300">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-zinc-500">{hint}</span>}
  </label>
);
